import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Per-route metadata. Wraps react-helmet-async with the things every page
 * should set: title, description, canonical URL, OG/Twitter cards.
 *
 * Defaults pull from the static index.html for anything not supplied so a
 * page only needs to override the one or two fields that differ.
 */

interface PageMetaProps {
  title: string;
  description: string;
  path?: string; // pathname like "/faq" — used to build the canonical URL
  image?: string; // full URL to a card image
  jsonLd?: Record<string, unknown>;
  // Renders BreadcrumbList JSON-LD (the only rich result that actually shows
  // for us today). Pass the trail excluding Home — e.g. [{name:"FAQ"}] on /faq.
  breadcrumbs?: Array<{ name: string }>;
}

const SITE_URL = "https://hal8580.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-card.png`;

export const PageMeta: React.FC<PageMetaProps> = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  jsonLd,
  breadcrumbs,
}) => {
  const canonical = `${SITE_URL}${path}`;
  const breadcrumbLd =
    breadcrumbs && breadcrumbs.length
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            {
              "@type": "ListItem",
              position: 2,
              name: breadcrumbs[breadcrumbs.length - 1].name,
              item: canonical,
            },
          ],
        }
      : null;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonical} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
      {breadcrumbLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbLd)}
        </script>
      )}
    </Helmet>
  );
};
